import { useNavigate } from 'react-router-dom'
import { useJobPoll, Job } from '../hooks/useJobPoll'
import { Check, X, ExternalLink } from 'lucide-react'

// Global floating indicator for the user's in-flight technology-discovery jobs.
// DEV-732: migrated from the legacy /api/discovery-jobs/ (DiscoveryJob) to the
// unified ai.Job framework via useJobPoll, filtered to kind='tech_discovery'.

const RING_SIZE = 48
const STROKE_WIDTH = 4
const RADIUS = (RING_SIZE - STROKE_WIDTH) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

type Tone = 'complete' | 'failed' | 'active'

function toneOf(job: Job): Tone {
  if (job.status === 'completed' || job.status === 'partial') return 'complete'
  if (job.status === 'failed' || job.status === 'canceled') return 'failed'
  return 'active'
}

function ProgressRing({ percentage, tone }: { percentage: number; tone: Tone }) {
  const offset = CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE

  const ringColor =
    tone === 'complete'
      ? 'stroke-emerald-500'
      : tone === 'failed'
        ? 'stroke-red-500'
        : 'stroke-primary'

  const bgColor =
    tone === 'complete'
      ? 'stroke-emerald-500/20'
      : tone === 'failed'
        ? 'stroke-red-500/20'
        : 'stroke-muted-foreground/20'

  return (
    <svg width={RING_SIZE} height={RING_SIZE} className="flex-shrink-0 -rotate-90">
      {/* Background ring */}
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RADIUS}
        fill="none"
        strokeWidth={STROKE_WIDTH}
        className={bgColor}
      />
      {/* Progress ring */}
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RADIUS}
        fill="none"
        strokeWidth={STROKE_WIDTH}
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className={`${ringColor} transition-[stroke-dashoffset] duration-700 ease-out`}
      />
    </svg>
  )
}

function RingCenter({ job, tone }: { job: Job; tone: Tone }) {
  if (tone === 'complete') {
    return <Check className="w-4 h-4 text-emerald-500" />
  }
  if (tone === 'failed') {
    return <X className="w-4 h-4 text-red-500" />
  }
  return (
    <span className="text-[10px] font-semibold text-foreground tabular-nums">
      {job.progress}%
    </span>
  )
}

function JobCard({
  job,
  onAcknowledge,
}: {
  job: Job
  onAcknowledge: (id: string) => void
}) {
  const navigate = useNavigate()
  const tone = toneOf(job)
  const isActive = tone === 'active'
  const isCompleted = tone === 'complete'
  const isFailed = tone === 'failed'
  const pct = isCompleted ? 100 : job.progress

  const handleClick = () => {
    if (isCompleted) {
      onAcknowledge(job.id)
      if (job.target_type === 'technology' && job.target_id) {
        navigate(`/market?tech=${job.target_id}&q=${encodeURIComponent(job.label)}`)
      }
    } else if (isFailed) {
      onAcknowledge(job.id)
    }
  }

  // Sub-progress text for the diligence fan-out (vendors + products).
  const subProgress =
    job.diligence_total > 0
      ? `${job.diligence_completed}/${job.diligence_total} tasks`
      : null

  return (
    <div
      onClick={isActive ? undefined : handleClick}
      className={`
        flex items-center gap-3 px-3 py-2.5 rounded-xl border shadow-lg backdrop-blur-sm
        min-w-[220px] max-w-[260px] text-left transition-all duration-200
        ${isActive
          ? 'border-border/60 bg-card/95'
          : isCompleted
            ? 'border-emerald-500/30 bg-card/95 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 cursor-pointer'
            : 'border-red-500/30 bg-card/95 hover:bg-red-50 dark:hover:bg-red-950/30 cursor-pointer'
        }
      `}
    >
      {/* Ring with center content */}
      <div className={`relative flex-shrink-0 ${isActive ? 'animate-pulse [animation-duration:3s]' : ''}`}>
        <ProgressRing percentage={pct} tone={tone} />
        <div className="absolute inset-0 flex items-center justify-center">
          <RingCenter job={job} tone={tone} />
        </div>
      </div>

      {/* Text content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="text-xs font-medium text-foreground truncate flex-1">
            {job.label}
          </p>
          {isCompleted && job.target_type === 'technology' && job.target_id && (
            <ExternalLink className="w-3 h-3 text-emerald-500 flex-shrink-0" />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onAcknowledge(job.id); }}
            className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground truncate mt-0.5">
          {isCompleted
            ? (job.target_type === 'technology' && job.target_id ? 'Click to view results' : 'Complete')
            : isFailed
              ? job.error_message || 'Research failed'
              : job.current_step_label
          }
        </p>
        {subProgress && isActive && (
          <p className="text-[10px] text-muted-foreground/70 mt-0.5">{subProgress}</p>
        )}
      </div>
    </div>
  )
}

export default function DiscoveryProgress() {
  const { jobs, acknowledgeJob } = useJobPoll({
    mine: true,
    unacknowledged: true,
    kind: 'tech_discovery',
  })

  if (jobs.length === 0) return null

  return (
    <div className="fixed bottom-[calc(var(--space-4)+var(--cui-banner-bottom))] left-4 z-40 flex flex-col-reverse gap-2">
      {jobs.map(job => (
        <JobCard key={job.id} job={job} onAcknowledge={acknowledgeJob} />
      ))}
    </div>
  )
}
